import { useEffect, useState, useCallback } from "react";
import { Plus, Target } from "lucide-react";
import { staffApi, apiError } from "../lib/api";

const FILTERS = ["", "On Track", "Complete", "Not Started", "Overdue"];
const STATUS_STYLE = {
  "Not Started": "bg-[#F3EFE0] text-[#566270]",
  "On Track": "bg-[#F6EED8] text-[#8a6a10]",
  "In Progress": "bg-[#F6EED8] text-[#8a6a10]",
  Complete: "bg-[#EBF6F0] text-[#2D6A4F]",
  Completed: "bg-[#EBF6F0] text-[#2D6A4F]",
  Overdue: "bg-[#FBF0F2] text-[#A62B2B]",
};

export default function QuestsPage() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [groups, setGroups] = useState([]);
  const [topics, setTopics] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", target_type: "notes", target_value: 300,
    topic_id: "", state_target: "Secure", assign_type: "institution", group_ids: [],
    deadline: "", reward_notes: 50,
  });
  const [error, setError] = useState("");

  const load = useCallback(() => {
    const params = filter ? `?status=${filter === "Complete" ? "Completed" : filter === "On Track" ? "In Progress" : filter}` : "";
    staffApi.get(`/staff/quests${params}`).then((r) => setRows(r.data.rows));
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    staffApi.get("/staff/groups").then((r) => setGroups(r.data.groups));
    staffApi.get("/staff/topics").then((r) => setTopics(r.data.topics));
  }, []); // mount-only: groups and topics lists

  const toggleGroup = (id) =>
    setForm((f) => ({ ...f, group_ids: f.group_ids.includes(id) ? f.group_ids.filter((g) => g !== id) : [...f.group_ids, id] }));

  const submit = async () => {
    setError("");
    try {
      await staffApi.post("/staff/quests", {
        title: form.title, description: form.description,
        target_type: form.target_type, target_value: Number(form.target_value),
        topic_id: form.topic_id || null, state_target: form.state_target,
        assign_type: form.assign_type, group_ids: form.group_ids,
        deadline: form.deadline ? new Date(form.deadline).toISOString() : null,
        reward_notes: Number(form.reward_notes),
      });
      setShowCreate(false);
      setForm({ title: "", description: "", target_type: "notes", target_value: 300, topic_id: "", state_target: "Secure", assign_type: "institution", group_ids: [], deadline: "", reward_notes: 50 });
      load();
    } catch (e) {
      setError(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="quests-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0B192C]">Quests</h1>
        <button onClick={() => setShowCreate(true)} data-testid="create-quest-btn"
          className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-[#C69B3D] text-[#0B192C] font-bold text-sm hover:bg-[#D8AB49] transition-colors min-h-[48px]">
          <Plus className="w-4 h-4" /> Set a Quest
        </button>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="quest-filters">
        {FILTERS.map((f) => (
          <button key={f || "all"} onClick={() => setFilter(f)} data-testid={`filter-${f || "all"}`}
            className={`px-4 py-2.5 rounded-full text-sm font-bold transition-colors min-h-[44px] ${filter === f ? "bg-[#0B192C] text-[#FAF7EE]" : "bg-white border border-[#E5DEC9] text-[#566270]"}`}>
            {f || "All"}
          </button>
        ))}
      </div>

      <div className="rounded-2xl bg-white border border-[#E5DEC9] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[680px]">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-[#566270] bg-[#FAF7EE] border-b border-[#E5DEC9]">
                <th className="px-5 py-3">Chorister</th><th className="px-5 py-3">Quest</th>
                <th className="px-5 py-3">Progress</th><th className="px-5 py-3">Deadline</th><th className="px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {(rows || []).map((r, i) => (
                <tr key={`${r.quest_id}-${r.chorister_id}`} className="border-b border-[#F3EFE0]" data-testid={`quest-row-${i}`}>
                  <td className="px-5 py-3 font-bold text-[#0B192C]">{r.chorister}</td>
                  <td className="px-5 py-3 text-[#1C242B]">{r.title}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 rounded-full bg-[#F3EFE0] overflow-hidden">
                        <div className="h-full bg-[#C69B3D]" style={{ width: `${Math.min(100, (r.progress / Math.max(1, r.target)) * 100)}%` }} />
                      </div>
                      <span className="font-note text-xs">{Math.min(r.progress, r.target)}/{r.target}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-[#566270]">{r.deadline ? new Date(r.deadline).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) : "—"}</td>
                  <td className="px-5 py-3">
                    <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${STATUS_STYLE[r.status] || ""}`}>{r.status === "Completed" ? "Complete" : r.status}</span>
                  </td>
                </tr>
              ))}
              {rows && rows.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-[#566270]">
                  <Target className="w-6 h-6 mx-auto mb-2 text-[#E5DEC9]" />No Quests match this filter.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-[#0B192C]/70 flex items-center justify-center p-4 z-50" role="dialog" aria-modal="true" data-testid="quest-dialog">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="font-display text-xl font-bold text-[#0B192C]">Set a Quest</h2>
            <div className="mt-4 space-y-3">
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Quest title, e.g. Earn 300 Notes by Friday" data-testid="quest-title-input"
                className="w-full rounded-xl border-2 border-[#E5DEC9] px-4 py-3 focus:border-[#C69B3D] focus:outline-none min-h-[48px]" />
              <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Friendly description (optional)" data-testid="quest-desc-input"
                className="w-full rounded-xl border-2 border-[#E5DEC9] px-4 py-3 focus:border-[#C69B3D] focus:outline-none min-h-[48px]" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-[#566270]" htmlFor="quest-type">Quest type</label>
                  <select id="quest-type" value={form.target_type} onChange={(e) => setForm({ ...form, target_type: e.target.value, target_value: e.target.value === "topic_mastery" ? 80 : e.target.value === "mastery_state" ? 1 : 300 })}
                    data-testid="quest-type-select" className="mt-1 w-full rounded-xl border-2 border-[#E5DEC9] px-3 py-3 bg-white min-h-[48px]">
                    <option value="notes">Notes Quest</option>
                    <option value="challenges">Challenge Quest</option>
                    <option value="topic_mastery">Topic Quest (reach % accuracy)</option>
                    <option value="mastery_state">Mastery Quest (reach a mastery level)</option>
                  </select>
                </div>
                <div>
                <label className="text-xs font-bold uppercase tracking-widest text-[#566270]" htmlFor="quest-target">
                  {form.target_type === "topic_mastery" ? "Target accuracy (%)" : form.target_type === "mastery_state" ? "Mastery level" : "Target"}
                </label>
                {form.target_type === "mastery_state" ? (
                  <select id="quest-target" value={form.state_target} onChange={(e) => setForm({ ...form, state_target: e.target.value })}
                    data-testid="quest-target-input" className="mt-1 w-full rounded-xl border-2 border-[#E5DEC9] px-3 py-3 bg-white min-h-[48px]">
                    {["Learning", "Practising", "Secure", "Mastered"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                ) : (
                  <input id="quest-target" type="number" min="1" value={form.target_value}
                    onChange={(e) => setForm({ ...form, target_value: e.target.value })} data-testid="quest-target-input"
                    className="mt-1 w-full rounded-xl border-2 border-[#E5DEC9] px-3 py-3 min-h-[48px]" />
                )}
              </div>
              {(form.target_type === "topic_mastery" || form.target_type === "mastery_state") && (
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-[#566270]" htmlFor="quest-topic">Topic</label>
                  <select id="quest-topic" value={form.topic_id} onChange={(e) => setForm({ ...form, topic_id: e.target.value })}
                    data-testid="quest-topic-select" className="mt-1 w-full rounded-xl border-2 border-[#E5DEC9] px-3 py-3 bg-white min-h-[48px]">
                    <option value="">Choose a topic…</option>
                    {topics.map((t) => (
                      <option key={t.id} value={t.id} disabled={!t.enabled}>
                        {t.enabled ? "" : "✕ (module off) "}{t.module_name}{t.grade ? (t.module_name?.toLowerCase().includes("theory") ? ` G${t.grade}` : ` L${t.grade}`) : ""} — {t.name}
                      </option>
                    ))}
                  </select>
                  {form.topic_id && topics.find((t) => t.id === form.topic_id)?.enabled === false && (
                    <p className="text-xs text-[#A62B2B] mt-1">That topic's module is disabled — enable it in Learning Modules before setting this Quest.</p>
                  )}
                </div>
              )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-[#566270]" htmlFor="quest-deadline">Deadline</label>
                  <input id="quest-deadline" type="date" value={form.deadline}
                    onChange={(e) => setForm({ ...form, deadline: e.target.value })} data-testid="quest-deadline-input"
                    className="mt-1 w-full rounded-xl border-2 border-[#E5DEC9] px-3 py-3 min-h-[48px]" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-[#566270]" htmlFor="quest-reward">Reward Notes</label>
                  <input id="quest-reward" type="number" min="0" value={form.reward_notes}
                    onChange={(e) => setForm({ ...form, reward_notes: e.target.value })} data-testid="quest-reward-input"
                    className="mt-1 w-full rounded-xl border-2 border-[#E5DEC9] px-3 py-3 min-h-[48px]" />
                </div>
              </div>
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-[#566270]">Assign to</span>
                <div className="mt-1 space-y-1.5">
                  <label className="flex items-center gap-2 text-sm min-h-[40px]">
                    <input type="radio" name="assign" checked={form.assign_type === "institution"}
                      onChange={() => setForm({ ...form, assign_type: "institution" })} data-testid="assign-institution" className="w-5 h-5 accent-[#C69B3D]" />
                    Whole institution
                  </label>
                  <label className="flex items-center gap-2 text-sm min-h-[40px]">
                    <input type="radio" name="assign" checked={form.assign_type === "groups"}
                      onChange={() => setForm({ ...form, assign_type: "groups" })} data-testid="assign-groups" className="w-5 h-5 accent-[#C69B3D]" />
                    Selected groups
                  </label>
                  {form.assign_type === "groups" && (
                    <div className="pl-7 space-y-1">
                      {groups.map((g) => (
                        <label key={g.id} className="flex items-center gap-2 text-sm min-h-[36px]">
                          <input type="checkbox" checked={form.group_ids.includes(g.id)} onChange={() => toggleGroup(g.id)}
                            data-testid={`quest-group-${g.id}`} className="w-5 h-5 accent-[#C69B3D]" />
                          {g.name}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {error && <p role="alert" className="text-sm text-[#A62B2B] mt-3" data-testid="quest-error">{error}</p>}
            <div className="flex gap-2 mt-5">
              <button onClick={submit} disabled={!form.title.trim()} data-testid="quest-submit"
                className="flex-1 px-4 py-3 rounded-xl bg-[#C69B3D] text-[#0B192C] font-bold min-h-[48px] disabled:opacity-50">
                Set Quest
              </button>
              <button onClick={() => { setShowCreate(false); setError(""); }} data-testid="quest-cancel"
                className="px-4 py-3 rounded-xl border-2 border-[#E5DEC9] font-bold min-h-[48px]">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
